<template>
  <div class="phone-input-container" :class="{ 'has-error': !isPhoneValid && phoneInput }">
    <div class="phone-input-label" v-if="label">{{ label }}</div>
    
    <div class="phone-input-wrapper">
      <div v-if="selectedCountry" class="country-select" @click="toggleDropdown">
        <span class="flag-container">
          <i :class="`fi fi-${selectedCountry.iso2.toLowerCase()}`"></i>
        </span>
        <span class="country-code">
          +{{ selectedCountry.phoneCode }}
        </span>
        <span class="dropdown-arrow">&#9660;</span>
      </div>
      <div v-if="loading" class="country-select">
        <span class="flag-container">
          <i class="pi pi-spin pi-spinner" ></i>
        </span>
        <span class="country-code">
          ...
        </span>
      </div>
      
      <input
        ref="phoneInputRef"
        type="tel"
        v-model="phoneInput"
        class="phone-field"
        :placeholder="getPlaceholder()"
        :disabled="props.disabled || loading"
        :readonly="props.readonly"
        @input="onPhoneInput"
        @focus="onFocus"
        @blur="onBlur"
      />
      
      <div v-if="showDropdown" class="country-dropdown">
        <div class="dropdown-search">
          <input 
            type="text" 
            v-model="searchQuery" 
            placeholder="Search country" 
            @input="filterCountries"
            @click.stop
          />
        </div>
        <div class="country-list">
          <div 
            v-for="country in filteredCountries" 
            :key="country.id" 
            class="country-option"
            @click.stop="selectCountry(country)"
          >
            <span class="option-flag">
              <i :class="`fi fi-${country.iso2.toLowerCase()}`"></i>
            </span>
            <span class="option-name">{{ country.description }}</span>
            <span class="option-code">+{{ country.phoneCode }}</span>
          </div>
        </div>
      </div>
    </div>
  </div>
</template>

<script setup>
import { ref, watch, onMounted, computed, nextTick, onUnmounted } from 'vue';
import { get } from '@/api/Services';
import { parsePhoneNumberFromString, AsYouType, getExampleNumber } from 'libphonenumber-js/max';
import examples from 'libphonenumber-js/mobile/examples';

// Define props
const props = defineProps({
  modelValue: {
    type: String,
    default: ''
  },
  phoneNumber: {
    type: String,
    default: ''
  },
  disabled: {
    type: Boolean,
    default: false
  },
  readonly: {
    type: Boolean,
    default: false
  },
  label: {
    type: String,
    default: ''
  }
});

// Define emits
const emit = defineEmits(['update:modelValue', 'update:phoneNumber', 'country-change', 'is-valid']);

// Refs
const countries = ref([]);
const filteredCountries = ref([]);
const selectedCountry = ref(null);
const phoneInput = ref('');
const loading = ref(true);
const isPhoneValid = ref(true);
const phoneInputRef = ref(null);
const showDropdown = ref(false);
const searchQuery = ref('');
const focused = ref(false);
const geoPermissionDenied = ref(false);

// Get placeholder based on selected country
const getPlaceholder = () => {
  if (!selectedCountry.value) return '(___) ___ - ____';
  
  try {
    const countryCode = selectedCountry.value.iso2;
    const example = getExampleNumber(countryCode, examples);
    if (example) {
      return example.formatNational().replace(/\d/g, '_');
    }
  } catch (e) {
    console.error('Error getting example:', e);
  }
  
  return '___-___-____';
};

// Toggle country dropdown
const toggleDropdown = () => {
  if (props.disabled || props.readonly) return;
  showDropdown.value = !showDropdown.value;
  searchQuery.value = '';
  filterCountries();
};

// Filter countries based on search query
const filterCountries = () => {
  if (!searchQuery.value.trim()) {
    filteredCountries.value = countries.value;
    return;
  }
  
  const query = searchQuery.value.toLowerCase();
  filteredCountries.value = countries.value.filter(country => 
    country.description.toLowerCase().includes(query) || 
    country.phoneCode.includes(query) ||
    country.iso2.toLowerCase().includes(query)
  );
};

// Select country and close dropdown
const selectCountry = (country) => {
  selectedCountry.value = country;
  showDropdown.value = false;
  
  // Format existing number with new country code
  if (phoneInput.value) {
    nextTick(() => {
      formatPhoneNumber(true);
    });
  }
  
  emit('country-change', country);
  updateModelValue();
};

// Handle focus event
const onFocus = () => {
  focused.value = true;
};

// Handle blur event
const onBlur = () => {
  focused.value = false;
  formatPhoneNumber(true); // Full formatting on blur
};

// Fetch countries from API
const fetchCountries = async () => {
  try {
    loading.value = true;
    const response = await get('/Countries');
    countries.value = response;
    filteredCountries.value = response;
    
    // Set default country (US) if available
    const defaultCountry = countries.value.find(c => c.iso2 === 'GB') || 
                           (countries.value.length > 0 ? countries.value[0] : null);
    if (defaultCountry && !selectedCountry.value) {
      selectedCountry.value = defaultCountry;
    }
    
    loading.value = false;
  } catch (error) {
    console.error('Error fetching countries:', error);
    loading.value = false;
  }
};

// Handle phone input change
const onPhoneInput = (event) => {
  formatPhoneNumber();
  validatePhone();
  updateModelValue();
};

// Format phone number based on country
const formatPhoneNumber = (fullFormat = false) => {
  if (!phoneInput.value || !selectedCountry.value) return;
  
  try {
    // Get the current digits only
    const digitsOnly = phoneInput.value.replace(/\D/g, '');
    if (!digitsOnly) return;
    
    // Create the full international number
    const fullNumber = `+${selectedCountry.value.phoneCode}${digitsOnly}`;
    
    // Parse the number and format it
    const phoneNumber = parsePhoneNumberFromString(fullNumber, selectedCountry.value.iso2);
    
    if (phoneNumber) {
      // On focus, use national format without country code (easier to edit)
      if (focused.value && !fullFormat) {
        try {
          phoneInput.value = phoneNumber.formatNational();
        } catch (e) {
          // Fallback to just digits if national format fails
          phoneInput.value = digitsOnly;
        }
      } 
      // On blur or full format, use international format but remove the country code part
      // as that's shown separately
      else {
        try {
          const formattedIntl = phoneNumber.formatInternational();
          // Remove the country code part (+XX ) from the formatted string
          const withoutCountryCode = formattedIntl.replace(new RegExp(`\\+${selectedCountry.value.phoneCode}\\s?`), '');
          phoneInput.value = withoutCountryCode;
        } catch (e) {
          // Fallback to reasonably formatted version if international format fails
          phoneInput.value = digitsOnly.replace(/(\d{3})(\d{3})(\d{4})/, '$1 $2 $3').trim();
        }
      }
    } else {
      // If parsing fails, at least do basic formatting
      if (digitsOnly.length > 6) {
        // Format numbers with more than 6 digits with spaces for readability
        phoneInput.value = digitsOnly.replace(/(\d{3})(\d{3})(\d+)/, '$1 $2 $3').trim();
      } else {
        phoneInput.value = digitsOnly;
      }
    }
  } catch (error) {
    console.error('Formatting error:', error);
    // Don't lose user input on errors
  }
};

// Validate phone number format
const validatePhone = () => {
  if (!phoneInput.value || !selectedCountry.value) {
    isPhoneValid.value = false;
    return;
  }
  
  try {
    const digitsOnly = phoneInput.value.replace(/\D/g, '');
    
    // Check if there are actual digits entered beyond the country code
    if (digitsOnly.length === 0) {
      isPhoneValid.value = false;
      return;
    }
    
    const countryCode = selectedCountry.value.iso2;
    const fullNumber = `+${selectedCountry.value.phoneCode}${digitsOnly}`;
    
    // Try to parse the phone number to check validity
    const parsedNumber = parsePhoneNumberFromString(fullNumber, countryCode);
    isPhoneValid.value = parsedNumber ? parsedNumber.isValid() : false;
  } catch (error) {
    console.error('Validation error:', error);
    isPhoneValid.value = false;
  }
};

// Watch for validation changes and emit the valid status
watch(isPhoneValid, (isValid) => {
  emit('is-valid', isValid);
});

// Update the model value with full phone number
const updateModelValue = () => {
  if (!selectedCountry.value) {
    emit('update:modelValue', '');
    emit('update:phoneNumber', '');
    return;
  }
  
  const countryCode = `+${selectedCountry.value.phoneCode}`;
  let phoneNumber = phoneInput.value.replace(/\D/g, ''); // Strip non-digits
  
  if (phoneNumber) {
    try {
      // Try to parse and format the phone number
      const parsedNumber = parsePhoneNumberFromString(
        `${countryCode}${phoneNumber}`, 
        selectedCountry.value.iso2
      );
      
      if (parsedNumber) {
        const formattedNumber = parsedNumber.format('E.164');
        emit('update:modelValue', formattedNumber);
        emit('update:phoneNumber', formattedNumber);
        return;
      }
    } catch (error) {
      console.error('Parsing error:', error);
    }
    
    // Fallback to simple concatenation
    const fullNumber = `${countryCode}${phoneNumber}`;
    emit('update:modelValue', fullNumber);
    emit('update:phoneNumber', fullNumber);
  } else {
    // If no digits are entered, emit empty value instead of just country code
    emit('update:modelValue', '');
    emit('update:phoneNumber', '');
  }
};

// Handle click outside to close dropdown
const handleClickOutside = (event) => {
  if (showDropdown.value && !event.target.closest('.phone-input-container')) {
    showDropdown.value = false;
  }
};

// Watch for changes in the modelValue prop
watch(() => props.modelValue || props.phoneNumber, (newValue) => {
  if (!newValue) {
    phoneInput.value = '';
    return;
  }
  
  console.log('Phone input received:', newValue);
  
  try {
    // Try to parse the phone number from the model value
    const parsedNumber = parsePhoneNumberFromString(newValue);
    
    if (parsedNumber) {
      const countryCode = parsedNumber.countryCallingCode;
      const nationalNumber = parsedNumber.nationalNumber;
      
      console.log('Successfully parsed phone:', { countryCode, nationalNumber });
      
      // Find the country by phone code
      const country = countries.value.find(c => c.phoneCode === countryCode);
      if (country) {
        console.log('Found matching country:', country.description);
        selectedCountry.value = country;
        
        // Format the national number
        const formatter = new AsYouType(country.iso2);
        phoneInput.value = formatter.input(nationalNumber);
      } else {
        console.warn('No matching country found for code:', countryCode);
      }
    } else {
      console.warn('Failed to parse phone number:', newValue);
    }
  } catch (error) {
    console.error('Parse error details:', {
      error: error.message,
      input: newValue,
      countries: countries.value.length
    });
    
    // Fallback to basic parsing
    if (newValue.startsWith('+')) {
      const match = newValue.match(/^\+(\d+)\s*(.*)$/);
      if (match) {
        const countryCode = match[1];
        const phone = match[2] || '';
        
        console.log('Basic parsing:', { countryCode, phone });
        
        const country = countries.value.find(c => c.phoneCode === countryCode);
        if (country) {
          selectedCountry.value = country;
          phoneInput.value = phone.trim();
        }
      }
    } else {
      // If no plus sign, try to use the current selected country
      if (selectedCountry.value) {
        phoneInput.value = newValue;
      }
    }
  }
}, { immediate: true });

// Setup event listeners
onMounted(async () => {
  await fetchCountries();
  document.addEventListener('click', handleClickOutside);
  
  // Look for the initial phone number after countries are loaded
  if (props.modelValue || props.phoneNumber) {
    const value = props.modelValue || props.phoneNumber;
    
    setTimeout(() => {
      // Re-trigger watch by reassigning the value
      if (value.startsWith('+') && countries.value.length > 0) {
        const parsedNumber = parsePhoneNumberFromString(value);
        if (parsedNumber) {
          const countryCode = parsedNumber.countryCallingCode;
          const country = countries.value.find(c => c.phoneCode === countryCode);
          if (country) {
            selectedCountry.value = country;
            phoneInput.value = parsedNumber.formatNational();
            validatePhone();
          }
        }
      }
    }, 100);
  } else {
    // Request geolocation permission and set country based on location
    requestGeolocation();
  }
});

// Get user geolocation and set country accordingly
const requestGeolocation = () => {
  if (navigator.geolocation) {
    navigator.geolocation.getCurrentPosition(
      async (position) => {
        try {
          geoPermissionDenied.value = false;
          const { latitude, longitude } = position.coords;
          // Use reverse geocoding to get country from coordinates
          const response = await fetch(`https://api.bigdatacloud.net/data/reverse-geocode-client?latitude=${latitude}&longitude=${longitude}&localityLanguage=en`);
          const data = await response.json();
          
          if (data && data.countryCode) {
            console.log('Countries:', countries.value.find(c => c.iso2 === data.countryCode));
            const country = countries.value.find(c => c.iso2 === data.countryCode);
            if (country) {
              selectedCountry.value = country;
              emit('country-change', country);
              updateModelValue();
            }
          }
        } catch (error) {
          console.error('Error getting location data:', error);
        }
      },
      (error) => {
        console.log('Geolocation permission denied or error:', error);
        // Set flag for permission denied
        if (error.code === 1) { // 1 = Permission denied
          geoPermissionDenied.value = true;
          selectedCountry.value = countries.value.find(c => c.iso2 === 'GB');
        }
        // Fall back to default country selection
      },
      { timeout: 10000, maximumAge: 600000 } // Options: 10s timeout, cache for 10min
    );
  }
};

// Clean up event listeners
onUnmounted(() => {
  document.removeEventListener('click', handleClickOutside);
});
</script>

<style lang="scss" scoped>
.phone-input-container {
  width: 100%;
  position: relative;
  
  .phone-input-label {
    font-size: 0.9rem;
    margin-bottom: 0.5rem;
    color: #333;
  }
  
  &.has-error {
    .phone-input-wrapper {
      border-color: #d80027;
      &:focus-within {
        border-color: #d80027;
        box-shadow: inset 0 0 0 1px #b00020, inset 0 0 0 1px #b00020,
        inset 0 0 0 1px #b00020, inset 0 0 0 1px #b00020 !important;
      }
    }
  }
  
  .phone-input-wrapper {
    display: flex;
    align-items: center;
    width: 100%;
    border: 1px solid #ced4da;
    border-radius: 6px;
    background-color: #fff;
    transition: all 0.2s ease;
    overflow: hidden;
    
    &:focus-within {
      border-color: #4285f4;
      box-shadow: 0 0 0 0.2rem rgba(66, 133, 244, 0.25);
    }
  }
  
  .country-select {
    display: flex;
    align-items: center;
    cursor: pointer;
    padding: 0 8px;
    white-space: nowrap;
    user-select: none;
    font-weight: 500;
    color: #212121;
    height: 100%;
    border-right: 1px solid #ced4da;
    
    .flag-container {
      margin-right: 4px;
      font-size: 1.2rem;
      
      i {
        vertical-align: middle;
      }
    }
    
    .country-code {
      font-size: 0.95rem;
      margin-right: 4px;
    }
    
    .dropdown-arrow {
      font-size: 0.7rem;
      opacity: 0.6;
      margin-left: 2px;
    }
  }
  
  .phone-field {
    flex: 1;
    height: 100%;
    padding: 0.75rem 0.75rem;
    border: none;
    outline: none;
    font-size: 1rem;
    background: transparent;
    color: #212121;
    
    &:disabled {
      background-color: #f9f9f9;
      opacity: 0.7;
      cursor: not-allowed;
    }
    
    &::placeholder {
      color: #9e9e9e;
    }
  }
  
  .country-dropdown {
    position: absolute;
    top: 100%;
    left: 0;
    width: 100%;
    max-width: 300px;
    max-height: 300px;
    background: #fff;
    border-radius: 6px;
    box-shadow: 0 5px 15px rgba(0, 0, 0, 0.15);
    z-index: 1000;
    margin-top: 5px;
    overflow: hidden;
    border: 1px solid #ced4da;
    
    .dropdown-search {
      padding: 8px;
      border-bottom: 1px solid #eee;
      
      input {
        width: 100%;
        padding: 8px 12px;
        border: 1px solid #ddd;
        border-radius: 6px;
        outline: none;
        
        &:focus {
          border-color: #4285f4;
          box-shadow: 0 0 0 0.2rem rgba(66, 133, 244, 0.25);
        }
      }
    }
    
    .geo-permission-notice {
      padding: 8px 12px;
      background-color: #fff3cd;
      border-bottom: 1px solid #ffeeba;
      color: #856404;
      font-size: 0.85rem;
      text-align: center;
      
      span {
        display: block;
        padding: 4px 0;
      }
    }
    
    .country-list {
      max-height: 250px;
      overflow-y: auto;
      
      &::-webkit-scrollbar {
        width: 6px;
      }
      
      &::-webkit-scrollbar-track {
        background: #f1f1f1;
      }
      
      &::-webkit-scrollbar-thumb {
        background: #ccc;
        border-radius: 3px;
      }
    }
    
    .country-option {
      display: flex;
      align-items: center;
      padding: 10px 12px;
      cursor: pointer;
      
      &:hover {
        background-color: #f5f5f5;
      }
      
      .option-flag {
        margin-right: 8px;
        font-size: 1.2rem;
        
        i {
          vertical-align: middle;
        }
      }
      
      .option-name {
        flex: 1;
        font-size: 0.9rem;
      }
      
      .option-code {
        color: #666;
        font-size: 0.85rem;
      }
    }
  }
  
  .error-message {
    display: block;
    color: #f44336;
    font-size: 0.8rem;
    margin-top: 4px;
  }
}
</style>